import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { isValidEmail } from '@cbgms/base/utils/validations'
import { makeStyles } from '@mui/styles'

interface EmailContentDisplayProps {
  loading: boolean
  selectedTemplate: string
  emailTemplates: { EmailTemplateUUID: string; TemplateName: string }[]
  handleTemplateChange: (event: any) => void
  subject: string
  setSubject: (value: string) => void
  sender: string
  setSender: (value: string) => void
  cc: string
  setCc: (value: string) => void
  bcc: string
  setBcc: (value: string) => void
  emailBody: string
  setEmailBody: (value: string) => void
  modalOpen: boolean
  handleModalOpen: () => void
  handleModalClose: () => void
  selectedItems: string[]
  tempSelectedItems: string[]
  handleItemToggle: (item: string) => void
  handleModalSave: () => void
  handleItemDelete: (item: string) => void
  handleSaveClick: () => void
  isSaveButtonDisabled: boolean
  emailAttachment: { EmailAttachmentUUID: string; EmailAttachmentName: string }[]
  placeholders: string[]
}

const useStyles = makeStyles({
  paperContainer: {
    backgroundColor: 'white'
  },
  tableContainer: {
    padding: 16,
    backgroundColor: 'white',
    overflowY: 'auto',
    height: '100%'
  }
})

const EmailContentDisplay: React.FC<EmailContentDisplayProps> = ({
  loading,
  selectedTemplate,
  emailTemplates,
  handleTemplateChange,
  subject,
  setSubject,
  sender,
  setSender,
  cc,
  setCc,
  bcc,
  setBcc,
  emailBody,
  setEmailBody,
  modalOpen,
  handleModalOpen,
  handleModalClose,
  selectedItems,
  tempSelectedItems,
  handleItemToggle,
  handleModalSave,
  handleItemDelete,
  handleSaveClick,
  isSaveButtonDisabled,
  emailAttachment,
  placeholders
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const classes = useStyles()
  const [isValid, setIsValid] = React.useState(false)
  const handleEmailChange = (setter: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value)
    setIsValid(isValidEmails(e.target.value))
  }
  const [tableHeight, setTableHeight] = useState<number>(window.innerHeight)
  const tableRef = React.useRef<HTMLDivElement>(null)
  const handleResize = useCallback(() => {
    if (tableRef.current) {
      setTableHeight(window.innerHeight - tableRef.current.getBoundingClientRect().top - 20)
    }
  }, [])

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  const isValidEmails = (emailValue: string) => {
    const emails = emailValue.split(';').map(email => email.trim())
    return emails.some(email => isValidEmail(email))
  }
  return (
    <Box data-testid='email_content'>
      <Typography variant='h6' className='typographyTitle'>
        {t('Customers.EmailTemplate')}
      </Typography>
      <Box>
        <Paper ref={tableRef} className={classes.paperContainer} style={{ height: tableHeight }}>
          {loading ? (
            <Box data-testid='loading' display='flex' justifyContent='center' alignItems='center' height='100%'>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box className={classes.tableContainer}>
                <Grid container spacing={2} alignItems='center' mb={2}>
                  <Grid item xs={9.6} sx={{ position: 'relative' }}>
                    <Box className='selectTamplatePlaceholderInnerBox'>
                      <Typography variant='caption' color='textSecondary'>
                        {t('labels.selectTemplate')}
                      </Typography>
                    </Box>
                    <Select
                      value={selectedTemplate}
                      onChange={handleTemplateChange}
                      fullWidth
                      size='small'
                      displayEmpty
                      className='selectTemplate'
                    >
                      <MenuItem value='' disabled>
                        {t('selectTemplate')}
                      </MenuItem>
                      {emailTemplates.map((template: { EmailTemplateUUID: string; TemplateName: string }) => (
                        <MenuItem key={template.EmailTemplateUUID} value={template.TemplateName}>
                          {template.TemplateName}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={1.2}>
                    <Button
                      variant='outlined'
                      color='primary'
                      fullWidth
                      startIcon={<ArrowBackIcon />}
                      onClick={() => history.push('/')}
                      data-testid='back-button'
                    >
                      {t('buttons.back')}
                    </Button>
                  </Grid>
                  <Grid item xs={1.2}>
                    <Button
                      variant='contained'
                      color='primary'
                      fullWidth
                      startIcon={<SaveIcon />}
                      className='saveButton'
                      onClick={handleSaveClick}
                      disabled={isSaveButtonDisabled || isValid}
                    >
                      {t('buttons.save')}
                    </Button>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('labels.subject')}
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      size='small'
                      InputLabelProps={{ required: true, classes: { asterisk: 'requiredLabel' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('labels.sender')}
                      value={sender}
                      onChange={handleEmailChange(setSender)}
                      size='small'
                      error={isValidEmails(sender)}
                      helperText={isValidEmail(sender) ? t('AppLabels.invalidEmail') : ''}
                      InputLabelProps={{ required: true, classes: { asterisk: 'requiredLabel' } }}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('labels.cc')}
                      value={cc}
                      onChange={handleEmailChange(setCc)}
                      size='small'
                      error={isValidEmails(cc)}
                      helperText={isValidEmails(cc) ? t('AppLabels.invalidEmail') : ''}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('labels.bcc')}
                      value={bcc}
                      onChange={handleEmailChange(setBcc)}
                      size='small'
                      error={isValidEmails(bcc)}
                      helperText={isValidEmails(bcc) ? t('AppLabels.invalidEmail') : ''}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box className='attachmentBox'>
                      <Typography variant='body1' className='attachmentLabel'>
                        {t('labels.attachment')}
                      </Typography>
                      <IconButton color='primary' component='span' onClick={handleModalOpen} data-testid='fetch_attachment_button'>
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Box className='attachmentContainer'>
                      {selectedItems.map((item, index) => (
                        <Paper key={index} className='attachmentPaper'>
                          <Box sx={{ py: 1 }} data-testid='email_attachment'>
                            {item}
                          </Box>
                          <IconButton size='small' onClick={() => handleItemDelete(item)} data-testid={`delete-attachment-${index}`}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Paper>
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={8} className='emailContentBox'>
                    <TextField
                      fullWidth
                      label={t('labels.emailContent')}
                      multiline
                      rows={10}
                      value={emailBody}
                      onChange={e => setEmailBody(e.target.value)}
                      size='small'
                      className='emailContentField'
                      InputLabelProps={{ required: true, classes: { asterisk: 'requiredLabel' } }}
                    />
                  </Grid>
                  <Grid item xs={4} className='emailContentBox'>
                    <Box className='placeholderBox'>
                      <Box className='placeholderInnerBox'>
                        <Typography variant='caption' color='textSecondary'>
                          {t('labels.placeholder')}
                        </Typography>
                      </Box>
                      <Box className='placeholderContentBox'>
                        {placeholders.length > 0 ? (
                          placeholders.map((placeholder, index) => (
                            <Typography key={index} variant='body2' color='textSecondary' data-testid={`placeholder-${index}`}>
                              {placeholder}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant='body2' color='textSecondary'>
                            {t('noPlaceholders')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Dialog open={modalOpen} onClose={handleModalClose} data-testid='attachment_modal'>
                <DialogTitle>{t('selectItems')}</DialogTitle>
                <DialogContent className='dialogContent'>
                  <List>
                    {emailAttachment.map(item => (
                      <ListItem key={item.EmailAttachmentName} disablePadding>
                        <ListItemButton onClick={() => handleItemToggle(item.EmailAttachmentName)} className='listItemButton'>
                          <ListItemIcon>
                            <Checkbox
                              edge='start'
                              checked={tempSelectedItems.includes(item.EmailAttachmentName)}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText primary={item.EmailAttachmentName} className='listItemText' />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleModalClose} color='primary' data-testid='attachment_modal_close'>
                    {t('buttons.cancel')}
                  </Button>
                  <Button onClick={handleModalSave} color='primary' data-testid='attachment_modal_save'>
                    {t('buttons.save')}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  )
}

export default EmailContentDisplay
